import { ContextMenuButton, Td, Tr } from 'app/components/Table';
import { MouseEvent } from 'react';
import { Row } from '@tanstack/react-table';
import {
  Contribution,
  ContributionClassName,
  ContributionClassesAndRolesDDL,
  ContributionsGroupedAndSortedList,
  ContributionsListItem,
} from 'types/contribution';
import { ListItemAvatar, ListItemText } from 'app/components/ChakraTableList';
import { getAvatarImage } from 'app/utils/utils';
import { TruncatedChips } from 'app/components/Core/TruncatedChips';
import { getChipRoleLabel } from './utils';

interface AudioTableRowProps {
  row: Row<ContributionsListItem>;
  handleOpenEditDialog: (contributionGuid?: string) => void;
  contributionClassesAndRoles: ContributionClassesAndRolesDDL;
  contextHandler: (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
  setDialogOpen: (props: {
    type: 'contributionsList';
    contributor: ContributionsListItem;
    contributionClassName: ContributionClassName;
  }) => void;
}

export const ContributionRow = ({
  row,
  contextHandler,
  handleOpenEditDialog,
  contributionClassesAndRoles,
  setDialogOpen,
}: AudioTableRowProps) => {
  const { getToggleExpandedHandler, parentId } = row;

  if (parentId === undefined) {
    return (
      <Tr
        zIndex={1}
        position="relative"
        role="row"
        clickable
        data-group
        sx={{
          _hover: {},
          bg: 'transparent !important',
        }}
      >
        <Td
          sx={{
            fontWeight: 500,
            overflow: 'visible',
            color: 'textSubdued',
          }}
        >
          {
            ((row.original as unknown) as ContributionsGroupedAndSortedList[0])
              .className
          }
        </Td>
        <Td />
        <Td />
        <Td />
        <Td
          role="button"
          sx={{
            position: 'absolute',
            right: 0,
            lineHeight: '48px',
            fontSize: 'xs',
            fontWeight: 500,
            color: 'textSubdued',
            _groupHover: { color: 'textBase' },
          }}
          onClick={getToggleExpandedHandler()}
        >
          {row.getIsExpanded() ? 'Hide section' : 'Show section'}
        </Td>
      </Tr>
    );
  }

  const contributor = row.original;
  const groupHeader = (row.getParentRow()?.original as any).className;

  return (
    <Tr
      zIndex={1}
      position="relative"
      role="row"
      data-group
      even
      clickable
      className={row.index % 2 === 0 ? 'even' : 'odd'}
      onClick={() =>
        setDialogOpen({
          type: 'contributionsList',
          contributor,
          contributionClassName: groupHeader,
        })
      }
      onContextMenu={contextHandler}
    >
      <Td sx={{ overflow: 'visible' }}>
        <ListItemAvatar
          initials={contributor?.profile?.initials || ''}
          colors={contributor.profile?.avatarColors}
          src={getAvatarImage(contributor?.profile?.profileImages)}
        />
      </Td>
      <Td sx={{ pl: 0 }}>
        <ListItemText
          primary={contributor.profile?.displayName || ''}
          title={contributor.profile?.displayName || ''}
          data-testid={`${groupHeader}-name`}
        />
      </Td>
      <Td>
        <TruncatedChips<Partial<Contribution>>
          items={contributor.contributions}
          getKey={(contribution) => `contribution-${contribution.guid}`}
          getLabel={(contribution) =>
            getChipRoleLabel(
              contribution,
              contributionClassesAndRoles.classRoles
            )
          }
          onClick={(contribution, e) => {
            e.stopPropagation();
            handleOpenEditDialog(contribution.guid || '');
          }}
        />
      </Td>
      <Td />
      <Td>
        <ContextMenuButton onClick={contextHandler} />
      </Td>
    </Tr>
  );
};
